import type { Position } from '../data/types'

/**
 * Deep equality comparison for JSON-serializable values
 * Uses JSON.stringify for comparison
 */
export function eq(a: any, b: any): boolean {
    return JSON.stringify(a) === JSON.stringify(b)
}

export function unique<T>(list: T[]) {
    return [...new Set(list)]
}

/**
 * Generic flood fill algorithm
 * Finds all connected items starting from initial items
 * Always visits neighbors of start items, 
 * but still checks start items with filter for adding
 */
export function floodFill<T>(
    start: T[], 
    getNeighbors: (item: T) => T[],
    filter?: (item: T) => boolean
): T[] {
    const visited = new Set<T>()
    const result: T[] = []

    const queue: T[] = [...start, ...start.map(item => getNeighbors(item)).flat()]

    for (let item = queue.shift(); item; item = queue.shift()) {
        if (visited.has(item)) continue
        visited.add(item)
        if (filter && !filter(item)) continue
        result.push(item)
        queue.push(...getNeighbors(item))
    }

    return result
}

/**
 * Calculate weighted center position from a list of tiles
 * If dropTile is provided, returns its position directly
 * Otherwise calculates the average position of all tiles
 */
export function getWeightedCenter<T extends { position: Position }>(
    tiles: T[],
    dropTile?: T
): Position {
    if (dropTile) {
        return dropTile.position
    }
    const avgX = tiles.reduce((s, t) => s + t.position.x, 0) / tiles.length
    const avgY = tiles.reduce((s, t) => s + t.position.y, 0) / tiles.length
    return { x: Math.round(avgX), y: Math.round(avgY) }
}

/**
 * Prepare positions in range 15 from center (31x31) grid
 * Uses Euclidean distance √(x²+y²) for range check
 * Sorts by distance (closest first), then clockwise from right (east)
 */
function preparePositionDeltas() {
    const positions: (Position & { d2: number, deg: number })[] = []
    for (let dy = -15; dy <= 15; dy++) {
        for (let dx = -15; dx <= 15; dx++) {
            const d2 = dx * dx + dy * dy
            const angle = Math.atan2(dy, dx)  // Clockwise in screen coords (Y down)
            let deg = Math.round(angle * 180 / Math.PI)
            if (deg < 0) deg += 360
            if (deg === 0) deg = 0
            if (d2 > 15 * 15) continue
            positions.push({ x: dx, y: dy, d2: d2, deg: deg })
        }
    }
    return positions
        .sort((a, b) => {
            return  (a.d2 - b.d2) || (a.deg - b.deg) || (b.d2 - a.d2)
        })
}

export const deltas = preparePositionDeltas()

/**
 * Seeded random number generator (mulberry32)
 * Returns a function that generates random numbers in [0, 1)
 */
export function seededRandom(seed: number): () => number {
    return function() {
        let t = seed += 0x6D2B79F5
        t = Math.imul(t ^ t >>> 15, t | 1)
        t ^= t + Math.imul(t ^ t >>> 7, t | 61)
        return ((t ^ t >>> 14) >>> 0) / 4294967296
    }
}
